// Cache eviction policy.
//
// We sit on top of the browser's Cache API + IndexedDB metadata. Browsers
// will evict entries under storage pressure on their own, but they do so
// per-origin and unpredictably. We enforce our own ceiling so a long-lived
// signage device never drifts toward the platform quota and triggers an
// emergency eviction mid-playback (which on some Chromium versions blanks
// the player for a frame).
//
// The policy:
//   1. Track total bytes via IndexedDB (sum of cached_assets.bytes).
//   2. After every successful sync, if total > MAX_CACHE_BYTES, evict
//      revisions in least-recently-cached order until below the ceiling.
//      Revisions referenced by the current bundle are NEVER evicted.
//   3. If quota.usage is reported and we're using > 75% of it, target a
//      smaller ceiling for the eviction pass — even when our own counter
//      hasn't crossed MAX_CACHE_BYTES.

const MEDIA_CACHE = 'signage-media-v1';

import {
  deleteAssetsForRevision,
  deleteRevision,
  getDb,
  listAssetsForRevision,
  listRevisionsForScreen,
  type CachedAssetRow,
  type CachedRevisionRow,
} from './cache-db';

/** Soft default ceiling. Most signage hardware has at least 8 GB free. */
export const DEFAULT_MAX_CACHE_BYTES = 4 * 1024 * 1024 * 1024;
const QUOTA_USE_RATIO = 0.75;

export interface EvictOptions {
  screenId: string;
  /** Revision IDs that must NOT be evicted (currently referenced bundle). */
  protectedRevisionIds: ReadonlySet<string>;
  /** Override the default ceiling. */
  maxBytes?: number;
  storage?: { quota?: number; usage?: number };
}

export interface EvictResult {
  /** Bytes accounted for in our own ledger after the pass. */
  totalBytesAfter: number;
  evictedRevisions: string[];
  evictedBytes: number;
}

/**
 * Run an eviction pass. Safe to call after every sync; bails out early when
 * we're already under the ceiling.
 */
export async function enforceCachePolicy(opts: EvictOptions): Promise<EvictResult> {
  const ceiling = computeCeiling(opts);
  const all = await listRevisionsForScreen(opts.screenId);
  const totals = await measureTotals(all);

  if (totals.total <= ceiling) {
    return {
      totalBytesAfter: totals.total,
      evictedRevisions: [],
      evictedBytes: 0,
    };
  }

  // LRU order: oldest cachedAt first. Default-protected revisions never get
  // evicted — they would just be re-downloaded on the next sync.
  const candidates = all
    .filter((r) => !opts.protectedRevisionIds.has(r.revisionId))
    .sort((a, b) => (a.cachedAt ?? 0) - (b.cachedAt ?? 0));

  // Build set of cache keys still in use by protected revisions so we don't
  // delete shared content-addressed entries.
  const protectedKeys = new Set<string>();
  for (const id of opts.protectedRevisionIds) {
    const assets = await listAssetsForRevision(id);
    for (const a of assets) protectedKeys.add(a.cacheKey);
  }

  let total = totals.total;
  const evicted: string[] = [];
  let evictedBytes = 0;

  for (const rev of candidates) {
    if (total <= ceiling) break;
    const assets = await listAssetsForRevision(rev.revisionId);
    const removable = assets.filter((a) => !protectedKeys.has(a.cacheKey));
    const bytes = removable.reduce((acc, a) => acc + (a.bytes || 0), 0);

    try {
      const cache = await caches.open(MEDIA_CACHE);
      await Promise.all(removable.map((a) => cache.delete(a.cacheKey)));
    } catch {
      /* ignore — Cache API may be unavailable in dev */
    }
    await deleteAssetsForRevision(rev.revisionId);
    await deleteRevision(rev.revisionId);

    total -= bytes;
    evictedBytes += bytes;
    evicted.push(rev.revisionId);
  }

  return {
    totalBytesAfter: Math.max(0, total),
    evictedRevisions: evicted,
    evictedBytes,
  };
}

function computeCeiling(opts: EvictOptions): number {
  const ceiling = opts.maxBytes ?? DEFAULT_MAX_CACHE_BYTES;
  const usage = opts.storage?.usage ?? 0;
  const quota = opts.storage?.quota ?? 0;
  if (quota > 0 && usage > 0) {
    const safeQuota = Math.floor(quota * QUOTA_USE_RATIO);
    return Math.min(ceiling, safeQuota);
  }
  return ceiling;
}

async function measureTotals(all: CachedRevisionRow[]) {
  let total = 0;
  for (const r of all) {
    const assets = await listAssetsForRevision(r.revisionId);
    total += assets.reduce((acc, a) => acc + (a.bytes || 0), 0);
  }
  return { total };
}

/**
 * Best-effort total cache size as known to our IndexedDB ledger. Reports
 * zero on platforms without IndexedDB. Used for diagnostics display.
 */
export async function readTotalCachedBytes(screenId: string): Promise<number> {
  try {
    const all = await listRevisionsForScreen(screenId);
    let total = 0;
    for (const r of all) {
      const assets = await listAssetsForRevision(r.revisionId);
      total += assets.reduce((acc, a) => acc + (a.bytes || 0), 0);
    }
    return total;
  } catch {
    return 0;
  }
}

/**
 * Wipe everything cached for a screen. Used by the admin "factory reset"
 * affordance so a stuck player can be told to reset its cache without a
 * physical visit.
 */
export async function wipeScreenCache(screenId: string): Promise<void> {
  const all = await listRevisionsForScreen(screenId);
  const cache = (await tryOpenCache()) ?? null;
  for (const rev of all) {
    const assets = await listAssetsForRevision(rev.revisionId);
    if (cache) {
      await Promise.all(assets.map((a: CachedAssetRow) => cache.delete(a.cacheKey).catch(() => false)));
    }
    await deleteAssetsForRevision(rev.revisionId);
    await deleteRevision(rev.revisionId);
  }
  // Drop bundle + meta keys.
  try {
    const db = await getDb();
    await db.delete('meta', `bundle:${screenId}`);
    await db.delete('meta', `meta:${screenId}`);
  } catch {
    /* ignore */
  }
}

async function tryOpenCache(): Promise<Cache | null> {
  try {
    if (typeof caches === 'undefined') return null;
    return await caches.open(MEDIA_CACHE);
  } catch {
    return null;
  }
}
